import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Divider,
} from '@mui/material';
import {
  CalendarToday,
  Pets,
  Favorite,
  Female,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';

interface HeatRegistrationViewProps {
  heatData: any;
}

const HeatRegistrationView: React.FC<HeatRegistrationViewProps> = ({ heatData }) => {
  const { t } = useTranslation(['animal', 'common', 'dashboard']);
  
  // Extract data - handling different structures
  const data = heatData?.data?.data || heatData?.data || {};
  const resumo = heatData?.resumo || heatData?.data?.resumo || '';
  const entityProfile = data?.entity_profile || {};
  
  // Extract specific fields
  const animalTag = data.animal_tag || entityProfile?.cattle_id?.tag || entityProfile?._key || t('common:not_informed');
  const handlingDate = data.handling_date || data.data_cio || t('common:not_informed');
  const observations = data.notes || data.observacoes || '';

  // Format date helper
  const formatDate = (dateString: string) => {
    if (dateString === t('common:not_informed')) return dateString;
    try {
      // Handle YYYY-MM-DD string explicitly to avoid timezone shifts
      if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString('pt-BR');
      }
      
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR');
    } catch {
      return dateString;
    }
  };

  return (
    <Box sx={{ p: 3, height: 'calc(100vh - 60px)', overflow: 'auto' }}>
      {/* Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box
              component="img"
              src="/gestores/reproducao.svg"
              alt={t('dashboard:sections.reproduction')}
              sx={{
                width: 32,
                height: 32,
              }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: DESIGN_TOKENS.colors.organization.farm,
                  mb: 0.5
                }}
              >
                {t('animal:heat_registration.title', 'Registro de Cio')}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: DESIGN_TOKENS.colors.text.secondary }}
              >
                {t('animal:heat_registration.success_message', 'Cio registrado com sucesso no sistema')}
              </Typography>
            </Box>
            <Chip
              label={t('common:success')}
              color="success"
              sx={{ 
                fontWeight: 600,
                backgroundColor: DESIGN_TOKENS.colors.brand.primary,
                color: 'white'
              }}
            />
          </Box>
          
          {/* Summary Chips */}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Chip
              icon={
                <Box
                  component="img"
                  src="/vaca.svg"
                  alt={t('common:alt_texts.animal')}
                  sx={{ width: 16, height: 16 }}
                />
              }
              label={`${t('common:labels.animal')}: ${animalTag}`}
              size="small"
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
            <Chip
              icon={<CalendarToday sx={{ width: 16, height: 16 }} />}
              label={formatDate(handlingDate)}
              size="small"
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Detailed Info */}
      <Card>
        <CardContent>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              mb: 3,
              color: DESIGN_TOKENS.colors.text.primary
            }}
          >
            {t('animal:heat_registration.details_title', 'Detalhes do Registro')}
          </Typography>
          
          <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
            {/* Animal Info */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Female sx={{ fontSize: 20, color: DESIGN_TOKENS.colors.text.secondary }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {t('animal:heat_registration.animal_id', 'Identificação do Animal')}
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {animalTag}
              </Typography>
            </Box>
            
            {/* Date Info */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CalendarToday sx={{ fontSize: 20, color: DESIGN_TOKENS.colors.text.secondary }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {t('animal:heat_registration.event_date', 'Data do Evento')}
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {formatDate(handlingDate)}
              </Typography>
            </Box>

            {/* Observations if present */}
            {observations && (
              <Box sx={{ gridColumn: '1 / -1' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    {t('common:labels.observations', 'Observações')}
                  </Typography>
                </Box>
                <Typography variant="body1">
                  {observations}
                </Typography>
              </Box>
            )}
          </Box>
          
          {/* Resume Text */}
          {resumo && (
            <>
              <Divider sx={{ my: 3 }} />
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 1 }}>
                  {t('common:labels.summary', 'Resumo')}
                </Typography>
                <Typography variant="body1" sx={{ fontStyle: 'italic', color: DESIGN_TOKENS.colors.text.secondary }}>
                  "{resumo}"
                </Typography>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default HeatRegistrationView;
